import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  type Step,
  WalkthroughDialogComponent,
  WalkthroughFacadeService,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
  WalkthroughType,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class JobDescriptionWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly i18n = inject(I18nService);
  private readonly walkthroughFacadeService = inject(WalkthroughFacadeService);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.WriteJobDescriptions;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-job-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_1.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_1.description',
          ),
          imageUrl: walkthroughAssetsResolver('job_description.svg'),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_1.cta_text',
          ),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-create-job-4',
      position: 2,
      walkThroughType: WalkthroughType.JobDescription,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_2.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-job-description-3',
      position: 3,

      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_3.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_3.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center' }],
        offset: { offsetX: -24, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-job-description-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_4.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }],
        offset: { offsetX: 0, offsetY: -24 },
      },
    },
    {
      id: 'walkthrough-step-job-description-5',
      position: 5,

      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_5.heading',
          ),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_5.description',
          ),
          ctaText: this.i18n.translate(
            'app.walkthrough.walkthroughs.job_description.step_5.cta_text',
          ),
          ctaIcon: 'open-in-new',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(
          state,
          this.subtask,
          ['offers'],
          false,
          true,
        );
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  private finishHandler(): void {
    this.walkthroughFacadeService.openHelpArticleLink('createJobs');
    this.navigationHelperService.clearJobEditorAndOpenDrawer(['offers']);
  }
}
